// The site's name and its cities: one place to rename things. GG Bharat is the platform (games, shows and events in
// 3D); each city's game has its own title: Chandigarh's is "Chandigarh: The City Beautiful" (ChallanCity, its first
// working title, lives on as a game mode in the works). Cities not built yet show as "coming soon".
export const SITE_NAME = 'GG Bharat';
export const SITE_TAGLINE = 'India’s worlds, built to play.';

/** the Chandigarh twin, counted (public/world/index.json → stats, the build of 3 November 2026) */
export const CHD_STATS = { buildings: 79920, roads: 11887, trees: 83978 };

/** Who runs the site: on the Contact page, the policies and the footer. Razorpay matches these to the KYC, so they
 *  must be the real, legal details. Every value marked FILL_IN shows as a highlighted gap on the site until it's set. */
export const BUSINESS = {
  /** the legal name: your own name (sole proprietor) or the registered company */
  // the legal entity isn't decided yet: the brand until then (change it to the KYC name before applying to Razorpay)
  legalName: 'GG Bharat',
  /** the brand people see */
  brand: SITE_NAME,
  address: '#2266, Sector 40-C, Chandigarh 160036, India',
  email: 'nexusgaming2266@gmail.com',
  phone: 'FILL_IN +91 phone',
  /** when support answers */
  hours: 'Monday to Saturday, 10:00–18:00 IST',
  /** the courts the terms are under */
  jurisdiction: 'Chandigarh, India',
  /** the date the policies last changed */
  policiesUpdated: '4 November 2026',
};

/** The one thing for sale (when the game opens): shown on the homepage and the Pricing page. */
export const PASS = {
  name: "Chandigarh Founder's Pass",
  /** in rupees, GST included */
  price: 499,
  kind: 'one-time payment, lifetime access to Chandigarh: The City Beautiful',
  /** what's in it */
  includes: [
    'Lifetime access to Chandigarh: The City Beautiful, all of it at 1:1',
    'Every update to the Chandigarh map, free',
    'Online play with friends: text and voice chat, private rooms',
    'Plays in the browser on phone and laptop: nothing to download',
  ],
  refundDays: 7,
};

/** Registering your in-game name on the waitlist (server/register.ts holds the real fee: REG_FEE_PAISE). */
export const REG = {
  /** in rupees, taxes included */
  fee: 49,
  refundDays: 7,
  /** the "already registered" count shows only from this many (a small number tells nobody anything) */
  showCountFrom: 50,
};

/** true while a value still needs filling in */
export const unfilled = (v: string) => v.startsWith('FILL_IN');

export interface City {
  id: string;
  /** the city */
  name: string;
  /** its game's title */
  game: string;
  lat: number;
  lon: number;
  /** metres above the sea: where its pin stands on the paper relief */
  elev: number;
  status: 'live' | 'soon';
  /** one line for its card */
  blurb: string;
  /** its colour on the map */
  accent: string;
  /** the picture on its card (public/) */
  shot?: string;
  /** what's in it so far (live cities) */
  places?: string[];
}

export const CITIES: City[] = [
  {
    id: 'chandigarh',
    name: 'Chandigarh',
    game: 'Chandigarh',
    lat: 30.7333,
    lon: 76.7794,
    elev: 350,
    status: 'live',
    blurb: "The City Beautiful: Le Corbusier's planned city at 1:1. Walk it, drive it, fly over it with your friends.",
    accent: '#ff7a1a',
    shot: '/site/shots/pu-sc.webp',
    places: ['Sector 17 Plaza', 'Sukhna Lake', 'Panjab University', 'The Rose Garden underpass', 'Sector 7 market'],
  },
  { id: 'delhi', name: 'Delhi', game: 'Dilli Drift', lat: 28.6139, lon: 77.209, elev: 216, status: 'soon', blurb: 'Connaught Place, India Gate and the Ring Road at rush hour.', accent: '#ff3d6e' },
  { id: 'mumbai', name: 'Mumbai', game: 'Local Rush', lat: 19.076, lon: 72.8777, elev: 14, status: 'soon', blurb: 'Marine Drive at night and the 9:11 fast to Churchgate.', accent: '#3dd6ff' },
  { id: 'bengaluru', name: 'Bengaluru', game: 'Silk Board Saga', lat: 12.9716, lon: 77.5946, elev: 920, status: 'soon', blurb: 'The junction every Bangalorean has a story about.', accent: '#9b6bff' },
  { id: 'jaipur', name: 'Jaipur', game: 'Pink Pursuit', lat: 26.9124, lon: 75.7873, elev: 431, status: 'soon', blurb: 'The Pink City, Hawa Mahal and the bazaars of the walled city.', accent: '#ff6fa8' },
  { id: 'amritsar', name: 'Amritsar', game: 'Golden Lanes', lat: 31.634, lon: 74.8723, elev: 234, status: 'soon', blurb: 'The Golden Temple, the old city lanes and a plate of kulcha.', accent: '#ffc14d' },
  { id: 'kolkata', name: 'Kolkata', game: 'Tram Town', lat: 22.5726, lon: 88.3639, elev: 9, status: 'soon', blurb: 'Howrah Bridge, yellow taxis and the last trams in India.', accent: '#ffd23d' },
  { id: 'hyderabad', name: 'Hyderabad', game: 'Charminar Chase', lat: 17.385, lon: 78.4867, elev: 542, status: 'soon', blurb: 'The old city round Charminar and the glass towers of Hitec City.', accent: '#19e59a' },
  { id: 'chennai', name: 'Chennai', game: 'Marina Mile', lat: 13.0827, lon: 80.2707, elev: 6, status: 'soon', blurb: 'Marina Beach at dawn and the temple towers of Mylapore.', accent: '#3d8bff' },
  { id: 'goa', name: 'Goa', game: 'Sunset Scooty', lat: 15.4909, lon: 73.8278, elev: 7, status: 'soon', blurb: 'Coast roads, a rented scooty and the sun going down over the sea.', accent: '#ff9a3d' },
  { id: 'shimla', name: 'Shimla', game: 'Mall Road', lat: 31.1048, lon: 77.1734, elev: 2205, status: 'soon', blurb: 'The Ridge, Mall Road and the toy train up the hills.', accent: '#7fe0ff' },
];

export const LIVE = CITIES.filter((c) => c.status === 'live');
